import React, { useState, useEffect } from "react";
import { Button, Input, Tag, message } from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    CrownOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
    organizationAPIWithErrorHandling,
    organizationHelpers,
} from "../../../api/organization/organization";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";
import OrganizationFormDrawer from "./OrganizationFormDrawer";
import StaffDetailDrawer from "../Staff/StaffDetailDrawer";

// const DEFAULT_VISIBLE_FILTERS = ["search"];
const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
    "code", "name", "budget_code", "description",
    "resident_count", "household_count", "manager", "status",
];

export default function OrganizationTable() {
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 1,
    });
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        // Mặc định xếp theo số khu phố tăng dần. Backend sort natural theo phần số
        // trong tên (xem OrganizationService::applySort) nên "Khu phố 8" đứng trước
        // "Khu phố 16", không bị sort chuỗi cho ra 01, 02, 10, 100, 11, 8.
        sort_by: "name",
        sort_order: "asc",
    });

    // Thêm mới / chỉnh sửa làm bằng Drawer ngay trên danh sách.
    // Xem chi tiết vẫn là trang riêng vì màn đó có cán bộ, khách hàng, đợt thu, thống kê.
    const [formOpen, setFormOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    // Xem nhanh cán bộ phụ trách ngay tại chỗ, không rời cây đơn vị đang xem
    const [managerId, setManagerId] = useState(null);
    const [managerOpen, setManagerOpen] = useState(false);

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    // Chuyển children_recursive -> children cho tree table
    const transformOrganizations = (data) => {
        return data.map((item) => ({
            ...item,
            children: item.children_recursive && item.children_recursive.length
                ? transformOrganizations(item.children_recursive)
                : [],
        }));
    };

    // Nhận object thay vì tham số vị trí: setFilters là bất đồng bộ nên bản cũ đọc lại
    // filters.sort_by ngay sau khi set luôn chậm một nhịp -> bấm sort cột phải bấm 2 lần
    // mới đúng chiều. Giờ nơi gọi truyền thẳng thứ vừa thay đổi.
    const fetchOrganizations = async ({
        page = 1,
        perPage = pagination.pageSize,
        search = searchText,
        status = filters.status,
        sortBy = filters.sort_by,
        sortOrder = filters.sort_order,
    } = {}) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: perPage,
                search: (search || "").trim(),
                status,
                sort_by: sortBy,
                sort_order: sortOrder,
            };

            const response =
                await organizationAPIWithErrorHandling.getOrganizations(params);

            const { organizations: organizationData, pagination: paginationData } =
                organizationHelpers.extractOrganizationList(response);

            const treeData = transformOrganizations(organizationData);

            setOrganizations(treeData);
            setPagination({
                current: paginationData.current,
                pageSize: paginationData.pageSize,
                total: paginationData.total,
                from: paginationData.from,
                to: paginationData.to,
                lastPage: paginationData.lastPage,
            });
        } catch (error) {
            message.error(error.message || "Không thể tải danh sách đơn vị");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableChange = (paginationConfig, tableFilters, sorter) => {
        // Tính tại chỗ rồi truyền thẳng, không đọc lại state vừa set.
        const sortBy = sorter?.field || filters.sort_by;
        const sortOrder = sorter?.field
            ? (sorter.order === "descend" ? "desc" : "asc")
            : filters.sort_order;

        let statusFilter = "";
        if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
            statusFilter = tableFilters.status[0] ? "active" : "inactive";
        }

        setFilters((prev) => ({
            ...prev,
            status: statusFilter,
            sort_by: sortBy,
            sort_order: sortOrder,
        }));

        fetchOrganizations({
            page: paginationConfig.current,
            perPage: paginationConfig.pageSize,
            status: statusFilter,
            sortBy,
            sortOrder,
        });
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchOrganizations({ search: value });
    };

    const handleReset = () => {
        setSearchText("");
        setFilters((prev) => ({ ...prev, status: "" }));
        fetchOrganizations({ search: "", status: "" });
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setEditingRecord(null);
    };

    const handleInfo = (record) => {
        navigate(`/organizations/information/${record.id}`);
    };

    const handleDelete = async (record) => {
        const confirmed = await confirmDelete({ entity: "đơn vị", name: record.name });
        if (!confirmed) return;

        try {
            await organizationAPIWithErrorHandling.deleteOrganization(record.id);
            message.success("Xóa đơn vị thành công");
            fetchOrganizations({ page: pagination.current });
        } catch (error) {
            message.error(error.message || "Không thể xóa đơn vị");
        }
    };

    const handleAdd = () => {
        setEditingRecord(null);
        setFormOpen(true);
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: { title: "STT", key: "index", width: 50, fixed: "left" },
        },
        {
            key: "code", label: "Mã đơn vị",
            column: {
                title: "Mã đơn vị", dataIndex: "code", key: "code", width: 100,
                render: (text) => <strong className="text-blue-600">{text}</strong>,
            },
        },
        {
            key: "name", label: "Tên đơn vị",
            column: { title: "Tên đơn vị", dataIndex: "name", key: "name", width: 150, sorter: true },
        },
        {
            key: "budget_code", label: "Mã ngân sách",
            column: {
                title: "Mã ngân sách", dataIndex: "budget_code", key: "budget_code",
                ellipsis: true, width: 120, render: (text) => text || "-",
            },
        },
        {
            key: "description", label: "Chi tiết",
            column: {
                title: "Chi tiết", dataIndex: "description", key: "description",
                ellipsis: true, width: 180, render: (text) => text || "-",
            },
        },
        {
            key: "resident_count", label: "Cư dân",
            column: {
                title: "Cư dân", dataIndex: "resident_count", key: "resident_count",
                width: 100, align: "center", render: (val) => (val ? val.toLocaleString() : "-"),
            },
        },
        {
            key: "household_count", label: "Hộ gia đình",
            column: {
                title: "Hộ gia đình", dataIndex: "household_count", key: "household_count",
                width: 100, align: "center", render: (val) => (val ? val.toLocaleString() : "-"),
            },
        },
        {
            key: "manager", label: "Quản lý",
            column: {
                title: "Quản lý", key: "manager", width: 150,
                render: (_, record) => {
                    const manager = record?.manager?.staff;
                    if (!manager) return <span>-</span>;
                    return (
                        <span
                            onClick={() => {
                                setManagerId(manager.id);
                                setManagerOpen(true);
                            }}
                            style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#0070D7" }}
                        >
                            <CrownOutlined style={{ color: "#A8681E" }} />
                            <span>{manager.name}</span>
                        </span>
                    );
                },
            },
        },
        {
            key: "status", label: "Trạng thái",
            column: {
                title: "Trạng thái", dataIndex: "status", key: "status", width: 130,
                render: (status) => (
                    <Tag color={status ? "green" : "red"}>
                        {status ? "Hoạt động" : "Không hoạt động"}
                    </Tag>
                ),
                filters: [
                    { text: "Hoạt động", value: true },
                    { text: "Không hoạt động", value: false },
                ],
                filterMultiple: false,
            },
        },
        actionColumnDef((record) => [
            { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
            { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
            { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
        ]),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 16, md: 10 },
            field: (
                <Input.Search
                    placeholder="Tìm kiếm theo tên, mã,..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                    allowClear
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!searchText;

    return (
        <BrandedPageLayout
            title="Đơn vị"
            subtitle="Quản lý cây đơn vị, thông tin ngân sách và người phụ trách."
            breadcrumbItems={[{ title: "Đơn vị" }, { title: "Danh sách" }]}
        >
            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleReset}
                onReload={() => fetchOrganizations({ page: pagination.current })}
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Thêm đơn vị
                    </Button>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={organizations}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có đơn vị"
            />

            <OrganizationFormDrawer
                open={formOpen}
                record={editingRecord}
                onClose={handleCloseForm}
                onSaved={() =>
                    // Sửa thì ở nguyên trang đang xem, thêm mới thì về trang 1
                    fetchOrganizations({ page: editingRecord ? pagination.current : 1 })
                }
            />

            <StaffDetailDrawer
                open={managerOpen}
                staffId={managerId}
                onClose={() => setManagerOpen(false)}
            />
        </BrandedPageLayout>
    );
}
